import StaleOverlay from "@/components/StaleOverlay";
import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";
import { Target, Leaf, HelpCircle, TrendingUp, TrendingDown, Minus } from "lucide-react";

const BUCKET_META = {
  meta:    { label: "Meta 歸因", Icon: Target,     cls: "border-orange-300 bg-orange-50",   iconCls: "text-orange-600" },
  organic: { label: "自然流量",  Icon: Leaf,       cls: "border-emerald-300 bg-emerald-50", iconCls: "text-emerald-600" },
  other:   { label: "其他",      Icon: HelpCircle, cls: "border-slate-300 bg-slate-50",     iconCls: "text-slate-500" },
};

function TrendPill({ cur, prev }) {
  if (!prev && !cur) return <span className="text-[11px] text-slate-400">—</span>;
  const diff = cur - prev;
  const pct = prev > 0 ? Math.round((diff / prev) * 100) : null;
  const Icon = diff > 0 ? TrendingUp : diff < 0 ? TrendingDown : Minus;
  const cls = diff > 0 ? "text-emerald-700" : diff < 0 ? "text-rose-600" : "text-slate-500";
  return (
    <span className={`inline-flex items-center gap-0.5 text-[11px] font-mono ${cls}`}>
      <Icon className="h-3 w-3" />
      {diff > 0 ? "+" : ""}{diff}{pct !== null && ` (${pct > 0 ? "+" : ""}${pct}%)`}
    </span>
  );
}

// G1 — default (總覽) = intentionally weekly widget (近 7 天 vs 上週); inside /line-insights it follows the page range
export default function HomeFriendPathsSection({ range, start, end }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const weekly = !range;
  const rk = range || "7d";

  useEffect(() => {
    setLoading(true);
    api.get("/line/insights/path-attribution", { params: rk === "custom" && start ? { range: rk, since: start, until: end } : { range: rk } })
      .then((r) => setData(r.data))
      .catch(() => setData({ overall: null }))
      .finally(() => setLoading(false));
  }, [rk, start, end]);
  const title = weekly ? "本週加好友來源 · 近 7 天（每週固定，對比上週）" : `加好友來源 · ${data?.range ? `${data.range.since} ~ ${data.range.until}` : ""}`;
  const prevLabel = weekly ? "上週" : "前期";

  if (!data) {
    return <Skeleton className="h-48 w-full" />;
  }
  const o = data.overall;
  if (!o || o.totals?.friend_adds === 0) {
    return (
      <Card className="bg-white border-slate-200 p-5" data-testid="home-friend-paths-empty">
        <div className="text-sm font-semibold text-slate-900 mb-2" data-testid="home-fp-title">{title}</div>
        <div className="text-xs text-slate-400" data-testid="home-friend-paths-empty-hint">部分資料未匯入 · <a href="/settings/line-sources#csv-status" className="text-[#F26A1F] hover:underline">前往匯入</a></div>
      </Card>
    );
  }

  // Top 3 paths across all buckets
  const allPaths = [
    ...(o.meta.paths || []).map((p) => ({ ...p, bucket: "meta" })),
    ...(o.organic.paths || []).map((p) => ({ ...p, bucket: "organic" })),
    ...(o.other.paths || []).map((p) => ({ ...p, bucket: "other" })),
  ].sort((a, b) => b.gained - a.gained).slice(0, 5);

  return (
    <StaleOverlay loading={loading} testid="home-fp-stale">
    <div className="space-y-3" data-testid="home-friend-paths-section">
      <div className="flex items-center justify-between">
        <div className="text-sm font-semibold text-slate-900" data-testid="home-fp-title">{title}</div>
        <Badge variant="outline" className="border-slate-300 text-slate-500 bg-white text-[10px]">
          共 {o.totals.friend_adds} 位新朋友
        </Badge>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {["meta", "organic", "other"].map((k) => {
          const b = o[k];
          const meta = BUCKET_META[k];
          const topPath = (b.paths || [])[0];
          const totalPrev = (b.paths || []).reduce((s, p) => s + (p.prev_gained || 0), 0);
          return (
            <Card key={k} className={`${meta.cls} border p-4`} data-testid={`home-fp-${k}`}>
              <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-800 mb-3">
                <meta.Icon className={`h-4 w-4 ${meta.iconCls}`} /> {meta.label}
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] text-slate-600">新朋友</span>
                  <span className="font-mono font-semibold text-lg text-slate-900">{b.friend_adds}</span>
                </div>
                {k === "meta" && (
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] text-slate-600">CPA</span>
                    <span className={`font-mono font-semibold ${b.cpa ? "text-emerald-700" : "text-slate-400"}`} data-testid="home-fp-meta-cpa">
                      {b.cpa ? `NT$ ${b.cpa.toLocaleString()}` : "—"}
                    </span>
                  </div>
                )}
                {topPath && (
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] text-slate-600">Top</span>
                    <span className="text-[11px] font-mono text-slate-800 truncate ml-2">{topPath.path} {topPath.gained}</span>
                  </div>
                )}
                <div className="flex items-center justify-between pt-1 border-t border-slate-200/50">
                  <span className="text-[11px] text-slate-500">vs {prevLabel}</span>
                  <TrendPill cur={b.friend_adds} prev={totalPrev} />
                </div>
              </div>
            </Card>
          );
        })}
      </div>
      {allPaths.length > 0 && (
        <Card className="bg-white border-slate-200 p-4" data-testid="home-fp-top-paths">
          <div className="text-xs font-semibold text-slate-900 mb-2">Top 5 路徑總覽</div>
          <table className="w-full text-xs">
            <thead>
              <tr className="text-slate-500 border-b border-slate-200">
                <th className="text-left py-1.5">路徑</th>
                <th className="text-right py-1.5">{weekly ? "本週" : "本期"}</th>
                <th className="text-right py-1.5">{prevLabel}</th>
                <th className="text-right py-1.5">變化</th>
              </tr>
            </thead>
            <tbody>
              {allPaths.map((p) => (
                <tr key={p.path} className="border-b border-slate-100 last:border-0" data-testid={`home-fp-row-${p.path}`}>
                  <td className="py-1.5 text-slate-800">{p.path}</td>
                  <td className="text-right font-mono text-slate-900">{p.gained}</td>
                  <td className="text-right font-mono text-slate-500">{p.prev_gained || 0}</td>
                  <td className="text-right"><TrendPill cur={p.gained} prev={p.prev_gained || 0} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
    </StaleOverlay>
  );
}
